import { useMemo } from 'react'
import { scaleLinear } from 'd3-scale'
import { line, area, curveMonotoneX } from 'd3-shape'
import { extent } from 'd3-array'

export interface SparklineProps {
  data: number[]
  width?: number
  height?: number
  color?: string
  showArea?: boolean
  strokeWidth?: number
  className?: string
}

export function Sparkline({
  data,
  width = 120,
  height = 32,
  color = 'var(--chart-1)',
  showArea = true,
  strokeWidth = 1.5,
  className = '',
}: SparklineProps) {
  const { linePath, areaPath } = useMemo(() => {
    if (data.length < 2) return { linePath: '', areaPath: '' }

    const xScale = scaleLinear().domain([0, data.length - 1]).range([1, width - 1])
    const [yMin, yMax] = extent(data) as [number, number]
    const padding = (yMax - yMin) * 0.1 || 1
    const yScale = scaleLinear().domain([yMin - padding, yMax + padding]).range([height - 1, 1])

    const lineGen = line<number>()
      .x((_, i) => xScale(i))
      .y((d) => yScale(d))
      .curve(curveMonotoneX)

    const areaGen = area<number>()
      .x((_, i) => xScale(i))
      .y0(height)
      .y1((d) => yScale(d))
      .curve(curveMonotoneX)

    return {
      linePath: lineGen(data) ?? '',
      areaPath: areaGen(data) ?? '',
    }
  }, [data, width, height])

  if (data.length < 2) return null

  return (
    <svg width={width} height={height} className={className} aria-hidden="true">
      {showArea && (
        <path d={areaPath} fill={color} opacity={0.1} />
      )}
      <path d={linePath} fill="none" stroke={color} strokeWidth={strokeWidth} />
    </svg>
  )
}
